/**
 * LicenseDiagnosis.jsx – Phase 2B.1
 * Superadmin Lizenz-Diagnose
 * 
 * Zeigt für jeden Kunden:
 * - Billing-Modell (Legacy / Billing V2)
 * - Effektives Paket
 * - Aktive Module (canonical keys + aufgelöste Aliase)
 * - Standortlimit / Mitarbeiterlimit
 * - JET-Status
 * 
 * Read-Only. Keine Änderungen.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import {
  Search, Loader2, RefreshCw, ChevronDown, ChevronUp,
  Users, MapPin, Package, Shield, CheckCircle2, XCircle,
  AlertTriangle, Info, Zap, Lock, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const BILLING_MODEL_LABELS = {
  legacy_v1: { label: 'Bestandstarif', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  billing_v2: { label: 'Billing V2', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  superadmin: { label: 'Superadmin', color: 'bg-purple-100 text-purple-700' },
};

const PACKAGE_LABELS = {
  starter: { label: 'Starter', color: 'text-green-700 bg-green-100 dark:bg-green-900/20' },
  business: { label: 'Business', color: 'text-blue-700 bg-blue-100 dark:bg-blue-900/20' },
  professional_ai: { label: 'Professional AI', color: 'text-purple-700 bg-purple-100 dark:bg-purple-900/20' },
  enterprise: { label: 'Enterprise', color: 'text-orange-700 bg-orange-100' },
  legacy: { label: 'Bestandstarif', color: 'text-gray-700 bg-gray-100 dark:bg-gray-800' },
};

function BillingBadge({ model }) {
  const s = BILLING_MODEL_LABELS[model] || BILLING_MODEL_LABELS.legacy_v1;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

function PackageBadge({ pkg }) {
  const s = PACKAGE_LABELS[pkg] || { label: pkg, color: 'text-gray-700 bg-gray-100' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

// ── Einzeldiagnose Modal ─────────────────────────────────────────────────────
function DiagnosisModal({ customerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/superadmin/license/diagnosis/${customerId}`, { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(e => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [customerId]);

  const d = data || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold">{d.company_name || '...'}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <BillingBadge model={d.billing_model} />
              {d.effective_package && <PackageBadge pkg={d.effective_package} />}
              {d.employees_over_limit > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
                  {d.employees_over_limit} MA über Limit
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-muted-foreground">✕</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            {/* Grundinfo */}
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label="Billing Model" value={d.billing_model} icon={Shield} />
              <InfoCard label="Display Name" value={d.display_name} icon={Package} />
              <InfoCard label="Effektives Paket" value={d.effective_package} icon={Package} />
              <InfoCard label="Gesellschafter" value={d.jet_status?.selected_company_group || '–'} icon={Zap} />
              {!d.is_legacy && <InfoCard label="Abrechnungsintervall" value={d.billing_interval || 'monatlich'} icon={Info} />}
              {!d.is_legacy && <InfoCard label="Catalog-Status" value={d.catalog_status} icon={CheckCircle2} />}
            </div>

            {/* Warnungen (Phase 2B.3) */}
            {d.warnings?.length > 0 && (
              <div className="space-y-1.5">
                {d.warnings.map((w, i) => (
                  <div key={i} className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700 dark:text-red-400 font-medium">{w}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Standorte (Phase 2B.3) */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-xl space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Standorte</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Gesamt angelegt</p>
                  <p className="font-semibold">{d.location_count ?? d.active_locations ?? '?'}</p>
                </div>
                {!d.is_legacy && (
                  <>
                    <div>
                      <p className="text-xs text-muted-foreground">Aktive Standorte</p>
                      <p className={`font-semibold ${d.location_limit_ok === false ? 'text-red-600' : ''}`}>
                        {d.active_locations ?? '?'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Draft-Standorte</p>
                      <p className="font-semibold text-amber-600">{d.draft_locations ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Gebuchte Slots</p>
                      <p className="font-semibold">{d.booked_location_slots ?? '?'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Enthaltene Basis-Standorte</p>
                      <p className="font-semibold">{d.included_base_locations ?? 1}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Kostenpflichtige Zusatz</p>
                      <p className="font-semibold">{d.extra_locations_booked ?? 0}</p>
                    </div>
                  </>
                )}
              </div>
              {!d.is_legacy && (
                <div className="flex items-center gap-2 mt-1">
                  {d.location_limit_ok ? (
                    <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> Standortlimit ok</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> Standortlimit ÜBERSCHRITTEN</span>
                  )}
                </div>
              )}
            </div>

            {/* Limits Mitarbeiter */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-xl space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Mitarbeiter</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Aktive Mitarbeiter</p>
                  <p className={`font-semibold ${d.employees_over_limit > 0 ? 'text-red-600' : ''}`}>
                    {d.active_employees}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Erlaubtes Limit</p>
                  <p className="font-semibold">{d.employee_limit ?? '?'}</p>
                </div>
                {!d.is_legacy && (
                  <>
                    <div>
                      <p className="text-xs text-muted-foreground">MA je Standort</p>
                      <p className="font-semibold">{d.employees_per_location ?? 15}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Add-on MA</p>
                      <p className="font-semibold">{d.addon_employees ?? 0}</p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{d.employee_limit_note}</p>
              {!d.is_legacy && d.employee_addons?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {d.employee_addons.map((a, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 rounded-full">
                      {typeof a === 'object' ? `${a.slug} ×${a.quantity || 1}` : a}
                    </span>
                  ))}
                </div>
              )}
              {!d.is_legacy && (
                <div className="flex items-center gap-2 mt-1">
                  {d.employee_limit_ok ? (
                    <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> Mitarbeiterlimit ok</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> Mitarbeiterlimit ÜBERSCHRITTEN</span>
                  )}
                </div>
              )}
            </div>

            {/* Module – Aktiv */}
            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                Aktive Module ({d.enabled_modules_canonical?.length || 0})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(d.enabled_modules_canonical || []).map(m => (
                  <span key={m} className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full">{m}</span>
                ))}
                {(d.enabled_modules_canonical || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">(keine aktiven Module)</span>
                )}
              </div>
            </div>

            {/* Module – Gesperrt (für V2-Kunden: im Package, aber höheres Paket nötig) */}
            {!d.is_legacy && d.blocked_modules?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Lock className="h-3.5 w-3.5 text-red-500" />
                  Gesperrte Module – manuelle Overrides ({d.blocked_modules.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {d.blocked_modules.map(m => (
                    <span key={m} className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-full">{m}</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Diese Module wurden manuell angefragt, sind aber nicht im aktuellen Paket enthalten.</p>
              </div>
            )}

            {/* Aliase */}
            {Object.keys(d.aliases_resolved || {}).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Info className="h-3.5 w-3.5" /> Aufgelöste Aliase
                </p>
                <div className="space-y-1">
                  {Object.entries(d.aliases_resolved || {}).map(([old, canonical]) => (
                    <div key={old} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground line-through">{old}</span>
                      <span>→</span>
                      <span className="font-medium text-green-700 dark:text-green-300">{canonical}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unbekannte Keys */}
            {d.unknown_modules?.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 rounded-xl">
                <p className="text-xs font-semibold text-amber-800">Unbekannte Modul-Keys:</p>
                <p className="text-xs text-amber-700">{d.unknown_modules.join(', ')}</p>
              </div>
            )}

            {/* Geplante Module */}
            {d.planned_modules?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Geplant (noch nicht verfügbar)</p>
                <div className="flex flex-wrap gap-1.5">
                  {d.planned_modules.map(m => (
                    <span key={m} className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full">{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* JET Status */}
            {(d.jet_status?.industry_edition_requested || d.jet_status?.jet_verified) && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 rounded-xl space-y-1">
                <p className="text-xs font-bold text-blue-800">JET Edition</p>
                <p className="text-xs text-blue-700">
                  Angefragt: {d.jet_status.industry_edition_requested || '–'} ·
                  Verifiziert: {d.jet_status.jet_verified ? 'Ja' : 'Nein'}
                </p>
              </div>
            )}

            {/* Legacy Info */}
            {d.is_legacy && d.legacy_info && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 rounded-xl space-y-1">
                <p className="text-xs font-bold text-gray-700">Legacy-Paket Info</p>
                <p className="text-xs text-muted-foreground">{d.legacy_info.note}</p>
                <p className="text-xs text-muted-foreground">Package ID: {d.legacy_info.package_id}</p>
              </div>
            )}

            {/* Rohe Module */}
            <div className="space-y-1">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Rohe DB-Module</p>
              <p className="text-xs text-muted-foreground font-mono break-all">
                {(d.enabled_modules_raw || []).join(' · ') || '(keine)'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoCard({ label, value, icon: Icon }) {
  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <p className="text-sm font-semibold truncate">{value || '–'}</p>
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────
export default function LicenseDiagnosis() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [markingLegacy, setMarkingLegacy] = useState(false);

  // ── Umgebungserkennung: Preview vs. Produktion ─────────────────────────────
  // Buttons "Alle → Legacy" und "Testfirmen T01–T15" sind AUSSCHLIESSLICH in der
  // Preview sichtbar. In der Produktionsumgebung (app.allflowx.de) werden sie
  // vollständig ausgeblendet. Die Backend-Endpoints sind zusätzlich serverseitig
  // durch ENVIRONMENT- und Stripe-Key-Prüfung geschützt.
  const IS_PREVIEW = process.env.REACT_APP_BACKEND_URL?.includes('preview.emergentagent.com') ||
                     process.env.REACT_APP_BACKEND_URL?.includes('localhost');

  const load = async (f = filter, s = search, p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 30 });
      if (f === 'legacy') params.set('billing_model', 'legacy_v1');
      if (f === 'billing_v2') params.set('billing_model', 'billing_v2');
      const res = await axios.get(`${API}/api/superadmin/license/diagnosis?${params}`, { headers: authHeader() });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter, page]); // eslint-disable-line

  const handleSetupTestFirms = async () => {
    if (!IS_PREVIEW) {
      toast.error('Testfirmen können ausschließlich in der Preview-Umgebung erstellt werden.');
      return;
    }
    if (!window.confirm('Testfirmen T01–T15 erstellen? Nur in Preview. Keine Produktionsdaten werden verändert.')) return;
    setSetupLoading(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/license/setup-test-firms-2b3`, {}, { headers: authHeader() });
      toast.success(`${res.data.created} Testfirmen erstellt, ${res.data.skipped} bereits vorhanden`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Fehler';
      toast.error(msg);
    } finally { setSetupLoading(false); }
  };

  const handleMarkAllLegacy = async () => {
    if (!IS_PREVIEW) {
      toast.error('Diese Funktion ist in der Produktionsumgebung gesperrt.');
      return;
    }
    if (!window.confirm('Alle Kunden OHNE billing_model auf legacy_v1 setzen?\n\nBilling-V2-Kunden werden NICHT verändert.\n\nNur in Preview verwenden.')) return;
    setMarkingLegacy(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/license/mark-all-legacy`, {}, { headers: authHeader() });
      toast.success(res.data.message);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Fehler';
      toast.error(msg);
    } finally { setMarkingLegacy(false); }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
        <div className="max-w-5xl mx-auto space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold">Lizenz-Diagnose</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Billing-Modell, Pakete, Module und Limits für alle Kunden
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Alle → Legacy und Testfirmen-Buttons: NUR in Preview sichtbar */}
              {IS_PREVIEW && (
                <>
                  <Button variant="outline" size="sm" onClick={handleMarkAllLegacy} disabled={markingLegacy} className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="mark-all-legacy-btn" title="Nur Preview: Kunden ohne billing_model auf legacy_v1 setzen">
                    {markingLegacy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
                    Alle → Legacy
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleSetupTestFirms} disabled={setupLoading} className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="setup-test-firms-btn" title="Nur Preview: Testfirmen T01–T15 erstellen">
                    {setupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    Testfirmen T01–T15
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} className="gap-1.5">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Filter + Suche */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'legacy', 'billing_v2'].map(f => (
              <button key={f} onClick={() => { setFilter(f); setPage(1); }}
                className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${filter === f ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 dark:border-gray-700 text-muted-foreground'}`}>
                {f === 'all' ? 'Alle' : f === 'legacy' ? 'Legacy' : 'Billing V2'}
              </button>
            ))}
            <span className="text-xs text-muted-foreground self-center">{total} Kunden</span>
          </div>

          {/* Tabelle */}
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="hidden sm:grid grid-cols-[3fr_1.5fr_1.5fr_1fr_1fr_auto] gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                <span>Firma</span>
                <span>Billing</span>
                <span>Paket</span>
                <span>Standorte</span>
                <span>Mitarbeiter</span>
                <span></span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {customers.map(c => (
                  <div key={c.customer_id}
                    className="grid grid-cols-[1fr_auto] sm:grid-cols-[3fr_1.5fr_1.5fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer transition-colors"
                    onClick={() => setSelectedCustomer(c.customer_id)}
                    data-testid={`license-row-${c.customer_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{c.company_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                    </div>
                    <div className="sm:hidden flex items-center gap-2">
                      <BillingBadge model={c.billing_model} />
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="hidden sm:block"><BillingBadge model={c.billing_model} /></div>
                    <div className="hidden sm:block"><PackageBadge pkg={c.effective_package} /></div>
                    <div className="hidden sm:flex items-center gap-1 text-sm">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{c.location_count}</span>
                      {c.extra_locations > 0 && <span className="text-xs text-muted-foreground">(+{c.extra_locations})</span>}
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-sm">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className={c.employees_over_limit > 0 ? 'text-red-600 font-semibold' : ''}>
                        {c.active_employees}/{c.employee_limit}
                      </span>
                    </div>
                    <div className="hidden sm:block">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {customers.length === 0 && (
                  <p className="text-center py-12 text-sm text-muted-foreground">Keine Kunden gefunden</p>
                )}
              </div>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}>Zurück</Button>
              <span className="text-sm text-muted-foreground self-center">Seite {page}/{pages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page >= pages}>Weiter</Button>
            </div>
          )}
        </div>
      </div>

      {/* Einzeldiagnose Modal */}
      {selectedCustomer && (
        <DiagnosisModal customerId={selectedCustomer} onClose={() => setSelectedCustomer(null)} />
      )}
    </DashboardLayout>
  );
}
